import { useState, useMemo, useCallback } from "react";
import { useGetEquipmentsInfinite } from "../../EquipmentsTab/service/queries";
import type { Equipment } from "../../EquipmentsTab/types/types";
import useDebounceValue from "@hooks/useDebounceValue";

interface SelectedEquipment {
	equipment: Equipment;
	count: number;
}

interface UseEquipmentSelectionProps {
	initialEquipments?: SelectedEquipment[];
}

interface EquipmentOption {
	value: string;
	label: string;
	isPlaceholder?: boolean;
}

export const useEquipmentSelection = ({ initialEquipments = [] }: UseEquipmentSelectionProps) => {
	const [selectedEquipments, setSelectedEquipments] = useState<SelectedEquipment[]>(initialEquipments);
	const [selectedEquipmentId, setSelectedEquipmentId] = useState<string>("");
	const [selectedEquipmentDraft, setSelectedEquipmentDraft] = useState<Equipment | null>(null);
	const [equipmentCount, setEquipmentCount] = useState<number>(1);
	const [editingEquipmentIndex, setEditingEquipmentIndex] = useState<number | null>(null);
	const [equipmentSearch, setEquipmentSearch] = useState("");
	const debouncedEquipmentSearch = useDebounceValue(equipmentSearch, 300);

	// Use infinite query to fetch equipment data
	const {
		data: equipmentsData,
		fetchNextPage,
		hasNextPage,
		isFetching,
		isFetchingNextPage,
		isLoading,
		error,
	} = useGetEquipmentsInfinite(10, debouncedEquipmentSearch);

	const isInitialEquipmentLoading = isLoading && debouncedEquipmentSearch === "";
	const isEquipmentSearchLoading =
		isFetching && debouncedEquipmentSearch !== "" && !isFetchingNextPage;
	const showInitialEquipmentError =
		Boolean(error) && debouncedEquipmentSearch === "" && !equipmentsData?.pages?.length;

	// Flatten all loaded equipment data
	const allEquipments = useMemo(() => {
		if (!equipmentsData?.pages) {
			return [];
		}

		const flattened = equipmentsData?.pages?.flatMap((page: unknown) => {
			const typedPage = page as { data?: { data?: Equipment[] } | Equipment[] | Equipment };

			// Handle different possible response structures
			if (
				typedPage.data &&
				typeof typedPage.data === "object" &&
				"data" in typedPage.data &&
				Array.isArray((typedPage.data as { data: Equipment[] }).data)
			) {
				return (typedPage.data as { data: Equipment[] }).data.map((equipment: Equipment) => ({
					...equipment,
					id: equipment.uuid, // Add id property for compatibility
				}));
			}
			// Handle direct array structure
			if (Array.isArray(typedPage.data)) {
				return typedPage.data.map((equipment: Equipment) => ({
					...equipment,
					id: equipment.uuid, // Add id property for compatibility
				}));
			}
			// Handle single equipment object
			if (
				typedPage.data &&
				typeof typedPage.data === "object" &&
				!Array.isArray(typedPage.data) &&
				"uuid" in typedPage.data
			) {
				return [
					{
						...(typedPage.data as Equipment),
						id: (typedPage.data as Equipment).uuid,
					},
				];
			}

			return [];
		});

		return flattened;
	}, [equipmentsData]);

	// Get available equipments (not already selected, except when editing)
	const availableEquipments = useMemo(() => {
		const filtered = allEquipments.filter((equipment: Equipment) => {
			// If we're editing, include the equipment being edited
			if (editingEquipmentIndex !== null) {
				const editingEquipment = selectedEquipments[editingEquipmentIndex];
				if (editingEquipment && editingEquipment?.equipment?.uuid === equipment?.uuid) {
					return true;
				}
			}
			// Otherwise, exclude already selected equipment
			return !selectedEquipments?.some(selected => selected?.equipment?.uuid === equipment?.uuid);
		});

		return filtered;
	}, [allEquipments, selectedEquipments, editingEquipmentIndex]);

	// Get all equipment options for dropdown
	const allEquipmentOptions = useMemo((): EquipmentOption[] => {
		const options = availableEquipments.map((eq: Equipment) => ({
			value: eq?.uuid,
			label: `${eq?.label ? ` ${eq?.label}` : ""}`,
		}));

		return options;
	}, [availableEquipments]);

	const resolveEquipmentById = useCallback(
		(equipmentId: string): Equipment | undefined => {
			const fromQuery = allEquipments.find((eq: Equipment) => eq?.uuid === equipmentId);
			if (fromQuery) {
				return fromQuery;
			}
			if (selectedEquipmentDraft?.uuid === equipmentId) {
				return selectedEquipmentDraft;
			}
			return undefined;
		},
		[allEquipments, selectedEquipmentDraft],
	);

	const handleSelectEquipmentId = useCallback(
		(equipmentId: string) => {
			setSelectedEquipmentId(equipmentId);
			const equipment = allEquipments.find((eq: Equipment) => eq?.uuid === equipmentId);
			if (equipment) {
				setSelectedEquipmentDraft(equipment);
			}
		},
		[allEquipments],
	);

	const hasMoreOptions = Boolean(hasNextPage);

	// Show all equipment loaded from the API so far (next page loads on scroll)
	const visibleEquipmentOptions = useMemo((): EquipmentOption[] => {
		if (!selectedEquipmentId || !selectedEquipmentDraft || selectedEquipmentDraft.uuid !== selectedEquipmentId) {
			return allEquipmentOptions;
		}
		if (allEquipmentOptions.some(option => option.value === selectedEquipmentId)) {
			return allEquipmentOptions;
		}
		return [
			{
				value: selectedEquipmentDraft.uuid,
				label: selectedEquipmentDraft.label ? ` ${selectedEquipmentDraft.label}` : "",
			},
			...allEquipmentOptions,
		];
	}, [allEquipmentOptions, selectedEquipmentDraft, selectedEquipmentId]);

	const loadMoreOptions = useCallback(() => {
		if (!hasNextPage || isFetchingNextPage || isFetching) {
			return;
		}
		void fetchNextPage();
	}, [fetchNextPage, hasNextPage, isFetching, isFetchingNextPage]);

	// Add new equipment
	const handleAddEquipment = () => {
		if (selectedEquipmentId && equipmentCount > 0) {
			const equipment = resolveEquipmentById(selectedEquipmentId);
			if (equipment) {
				const newEquipment = {
					equipment,
					count: equipmentCount,
				};
				setSelectedEquipments([...selectedEquipments, newEquipment]);
				// Reset form
				setSelectedEquipmentId("");
				setSelectedEquipmentDraft(null);
				setEquipmentCount(1);
				setEquipmentSearch("");
			}
		}
	};

	// Start editing equipment
	const handleStartEdit = (index: number) => {
		const equipment = selectedEquipments?.[index];
		setEditingEquipmentIndex(index);
		setSelectedEquipmentId(equipment?.equipment?.uuid);
		setSelectedEquipmentDraft(equipment?.equipment ?? null);
		setEquipmentCount(equipment?.count);
	};

	// Save edited equipment
	const handleSaveEdit = () => {
		if (editingEquipmentIndex !== null && selectedEquipmentId && equipmentCount > 0) {
			const equipment = resolveEquipmentById(selectedEquipmentId);
			if (equipment) {
				const updated = [...selectedEquipments];
				updated[editingEquipmentIndex] = {
					equipment,
					count: equipmentCount,
				};
				setSelectedEquipments(updated);
				handleCancelEdit();
			}
		}
	};

	// Cancel editing
	const handleCancelEdit = () => {
		setEditingEquipmentIndex(null);
		setSelectedEquipmentId("");
		setSelectedEquipmentDraft(null);
		setEquipmentCount(1);
	};

	// Remove equipment
	const handleRemoveEquipment = (index: number) => {
		setSelectedEquipments(selectedEquipments?.filter((_, i) => i !== index));
		// If we're editing this equipment, cancel the edit
		if (editingEquipmentIndex === index) {
			handleCancelEdit();
		}
	};

	// Reset all equipment selection
	const resetEquipmentSelection = () => {
		setSelectedEquipments([]);
		setSelectedEquipmentId("");
		setSelectedEquipmentDraft(null);
		setEquipmentCount(1);
		setEditingEquipmentIndex(null);
		setEquipmentSearch("");
	};

	// Computed values
	const canAddEquipment =
		availableEquipments?.length > 0 ||
		editingEquipmentIndex !== null ||
		equipmentSearch.trim().length > 0 ||
		Boolean(selectedEquipmentId);
	const canConfirmAction = Boolean(selectedEquipmentId && equipmentCount > 0);
	const isEditing = editingEquipmentIndex !== null;
	const totalEquipmentCount = selectedEquipments?.reduce((sum, item) => sum + item?.count, 0);

	return {
		// State
		selectedEquipments,
		selectedEquipmentId,
		equipmentCount,
		editingEquipmentIndex,
		availableEquipments,
		visibleEquipmentOptions,
		hasMoreOptions,
		isFetching,
		isFetchingNextPage,
		isInitialEquipmentLoading,
		isEquipmentSearchLoading,
		showInitialEquipmentError,
		error,

		// Actions
		handleSelectEquipmentId,
		setEquipmentCount,
		handleAddEquipment,
		handleStartEdit,
		handleSaveEdit,
		handleCancelEdit,
		handleRemoveEquipment,
		loadMoreOptions,
		resetEquipmentSelection,
		setEquipmentSearch,

		// Computed
		canAddEquipment,
		canConfirmAction,
		isEditing,
		totalEquipmentCount,
	};
};
